import { datadogLogs } from "@datadog/browser-logs";

export type DatadogPayload = Record<string, unknown>;

type DatadogSdk = Pick<
  typeof datadogLogs,
  "init" | "logger" | "setUser" | "clearUser" | "setGlobalContext"
>;

export type DatadogClientConfig = {
  clientToken?: string;
  environment: "development" | "production";
  service: string;
  version: string;
};

export type DatadogClientDependencies = {
  sdk?: DatadogSdk;
};

export const createDatadogClient = (
  config: DatadogClientConfig,
  dependencies: DatadogClientDependencies = {}
) => {
  const sdk = dependencies.sdk || datadogLogs;
  let started = false;

  return {
    start: () => {
      if (started || config.environment === "development" || !config.clientToken) {
        return;
      }
      sdk.init({
        clientToken: config.clientToken,
        site: "datadoghq.com",
        forwardErrorsToLogs: true,
        service: config.service,
        env: config.environment,
        version: config.version,
        sessionSampleRate: 100,
      });
      started = true;
    },

    info: (message: string, payload: DatadogPayload) => {
      if (started) sdk.logger.info(message, payload);
    },

    error: (message: string, payload: DatadogPayload) => {
      if (started) sdk.logger.error(message, payload);
    },

    identify: (userId: string) => {
      if (started) sdk.setUser({ id: userId });
    },

    clearIdentity: () => {
      if (started) sdk.clearUser();
    },

    setContext: (context: DatadogPayload) => {
      if (started) sdk.setGlobalContext(context);
    },
  };
};

export type DatadogClient = ReturnType<typeof createDatadogClient>;
